// PR-35: Distill flow on mobile. Mirrors desktop DistillModal:
//   - parent kicks off api.distillThread(threadId, onEvent, mode)
//   - while streaming we show a "Scanning… N items" progress
//   - on `done` we switch to a per-item review list with checkboxes
//   - Commit fires the agent with the user's selection (parent handles)
//
// Differences vs desktop:
//   - fullscreen (no anchored modal — phones don't have room)
//   - rationale + body shown inline (no separate hover tooltip)
//   - mode picker is the topbar segmented control (just like desktop) but
//     stacked above the list rather than inline
//
// Closes via the × button in the header. Esc inside the review list also
// closes; while streaming, Esc is a "cancel and hide" hint (the stream
// keeps going server-side, but the user has dismissed the modal).

import { useEffect, useState } from 'react'
import type { CardProposal, DistillMode, GapProposal } from '../../api'

interface Props {
  open: boolean
  itemsSoFar: number
  cards: CardProposal[] | null
  gaps: GapProposal[] | null
  errorMsg: string | null
  mode: DistillMode
  /** True while the stream is in flight. Used to disable the mode picker
   *  so we don't fire two concurrent runs. */
  busy: boolean
  onChangeMode: (next: DistillMode) => void
  onClose: () => void
  onAccept: (chosen: { cards: CardProposal[]; gaps: GapProposal[] }) => void
}

const MODE_LABEL: Record<DistillMode, string> = {
  rescue: 'Rescue',
  decompose: 'Decompose',
  audit: 'Audit',
}

const MODE_HINT: Record<DistillMode, string> = {
  rescue: 'Capture missed crystallisations · light gap bias.',
  decompose: 'Explode listicles / hierarchies into atomic items.',
  audit: 'Project-wide gap inventory with priority.',
}

export function MobileDistillModal({
  open,
  itemsSoFar,
  cards,
  gaps,
  errorMsg,
  mode,
  busy,
  onChangeMode,
  onClose,
  onAccept,
}: Props) {
  const [selectedCards, setSelectedCards] = useState<Set<number>>(new Set())
  const [selectedGaps, setSelectedGaps] = useState<Set<number>>(new Set())

  // Pre-check everything on arrival.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (cards) setSelectedCards(new Set(cards.map((_, i) => i)))
  }, [cards])
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (gaps) setSelectedGaps(new Set(gaps.map((_, i) => i)))
  }, [gaps])

  // Esc closes (delegates the actual close decision to the parent — mid-
  // stream "cancel" is a UX-level dismiss, not a true cancel; the request
  // continues but the user has moved on).
  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [open, onClose])

  if (!open) return null

  const toggleCard = (i: number) =>
    setSelectedCards((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })
  const toggleGap = (i: number) =>
    setSelectedGaps((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })

  const accept = () => {
    if (!cards || !gaps) return
    onAccept({
      cards: cards.filter((_, i) => selectedCards.has(i)),
      gaps: gaps.filter((_, i) => selectedGaps.has(i)),
    })
  }

  const ready = cards !== null && gaps !== null
  const totalSelected = selectedCards.size + selectedGaps.size

  return (
    <div className="mobile-modal" role="dialog" aria-label="Distill">
      <header className="mobile-modal__head">
        <button
          type="button"
          className="mobile-peek__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <span className="mobile-modal__title">Distill</span>
      </header>

      <div className="mobile-distill__modes">
        {(Object.keys(MODE_LABEL) as DistillMode[]).map((m) => (
          <button
            key={m}
            type="button"
            className={
              'mobile-distill__mode' +
              (mode === m ? ' mobile-distill__mode--active' : '')
            }
            onClick={() => {
              if (busy || mode === m) return
              onChangeMode(m)
            }}
            disabled={busy && mode !== m}
          >
            {MODE_LABEL[m]}
          </button>
        ))}
      </div>
      <div className="mobile-distill__hint">{MODE_HINT[mode]}</div>

      <div className="mobile-distill__body">
        {errorMsg && (
          <div className="mobile-toast mobile-toast--error" role="alert">
            {errorMsg}
          </div>
        )}

        {!ready ? (
          <div className="mobile-distill__progress mono">
            Scanning thread…{' '}
            {itemsSoFar > 0 ? `${itemsSoFar} item${itemsSoFar === 1 ? '' : 's'}` : ''}
          </div>
        ) : (
          <>
            {(cards?.length ?? 0) === 0 && (gaps?.length ?? 0) === 0 ? (
              <div className="mobile-distill__empty">
                No new items found. The thread is already crystallised.
              </div>
            ) : (
              <>
                {(cards?.length ?? 0) > 0 && (
                  <section className="mobile-distill__section">
                    <h3 className="mobile-distill__section-title">
                      Cards ({selectedCards.size}/{cards?.length ?? 0})
                    </h3>
                    {cards?.map((c, i) => (
                      <label
                        key={`c${i}`}
                        className="mobile-distill__row"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCards.has(i)}
                          onChange={() => toggleCard(i)}
                        />
                        <div className="mobile-distill__row-content">
                          <div className="mobile-distill__row-head">
                            <span className="mobile-distill__row-kind mono">
                              {c.kind}
                            </span>
                            <span className="mobile-distill__row-title">
                              {c.title}
                            </span>
                          </div>
                          {c.body && (
                            <div className="mobile-distill__row-body">{c.body}</div>
                          )}
                          {c.rationale && (
                            <div className="mobile-distill__row-why">
                              why: {c.rationale}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </section>
                )}

                {(gaps?.length ?? 0) > 0 && (
                  <section className="mobile-distill__section">
                    <h3 className="mobile-distill__section-title">
                      Gaps ({selectedGaps.size}/{gaps?.length ?? 0})
                    </h3>
                    {gaps?.map((g, i) => (
                      <label
                        key={`g${i}`}
                        className="mobile-distill__row mobile-distill__row--gap"
                      >
                        <input
                          type="checkbox"
                          checked={selectedGaps.has(i)}
                          onChange={() => toggleGap(i)}
                        />
                        <div className="mobile-distill__row-content">
                          <div className="mobile-distill__row-head">
                            <span className="mobile-distill__row-kind mono">
                              → {g.proposed_kind}
                            </span>
                            {g.priority && (
                              <span className="mobile-distill__row-priority mono">
                                {g.priority}
                              </span>
                            )}
                            <span className="mobile-distill__row-title">
                              {g.title}
                            </span>
                          </div>
                          {g.body && (
                            <div className="mobile-distill__row-body">{g.body}</div>
                          )}
                          <div className="mobile-distill__row-why">
                            why: {g.ambiguity_reason}
                          </div>
                          {g.blocks && g.blocks.length > 0 && (
                            <div className="mobile-distill__row-blocks mono">
                              blocks: {g.blocks.join(', ')}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </section>
                )}
              </>
            )}
          </>
        )}
      </div>

      {ready && ((cards?.length ?? 0) > 0 || (gaps?.length ?? 0) > 0) && (
        <footer className="mobile-modal__foot">
          <button
            type="button"
            className="mobile-distill__cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="mobile-distill__commit"
            onClick={accept}
            disabled={totalSelected === 0}
          >
            Commit ({totalSelected})
          </button>
        </footer>
      )}
    </div>
  )
}
